import { writable, type Writable } from "svelte/store"
import { browser } from "$app/environment"
import { toasts } from "$lib/stores/toastStores"
import type { ILastWin } from "$lib/interfaces/ILastWin"
import { openDialog } from "$lib/stores/dialogStores"

export const isConnected: Writable<boolean> = writable(false)
export const messages: Writable<any[]> = writable([])
export const lastWins: Writable<ILastWin> = writable()

let ws: WebSocket
const reconnectInterval = 5000 // Time in milliseconds before attempting to reconnect
let reconnectTimeoutId: number
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5
let pingInterval: number
let sUrl = ""

export function initWebSocket(url?: string): () => void {
  if (ws) {
    ws.close()
    clearInterval(pingInterval)
  }

  if (url) {
    sUrl = url
  }

  if (!browser || !sUrl) return () => {}

  try {
    ws = new WebSocket(sUrl)

    ws.onopen = (event) => {
      console.log("WebSocket connection opened:", event)
      isConnected.set(true)
      reconnectAttempts = 0

      // Setup ping interval
      pingInterval = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping")
        }
      }, 20000)
    }

    ws.onmessage = (event) => {
      if (event.data === "") return

      // Handle special case messages that aren't JSON
      if (event.data === "pong" || event.data === "ping") {
        // These are heartbeat messages, no need to process them
        return
      }

      try {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case "BONUS_WAGER_SUCCESS":
            toasts.send(data.content, "success")
            break
          case "LATEST_BETS":
            lastWins.set(data.data)
            break
          case "DEPOSIT":
          case "DEPOSIT_PENDING":
            toasts.send(data.content, "success", 7000, () =>
              openDialog("TransactionDetailsModal", data.data.transactionOrderId),
            )
            break
          case "REFRESH_NOTIFICATIONS":
            // Handle notifications refresh
            break
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error, "Raw message:", event.data)
      }
    }

    ws.onerror = (error) => {
      console.error("WebSocket Error:", error)
      isConnected.set(false)
      attemptReconnect()
    }

    ws.onclose = (event) => {
      isConnected.set(false)
      clearInterval(pingInterval)

      if (!event.wasClean) {
        attemptReconnect()
      }
    }
  } catch (error) {
    console.error("WebSocket initialization failed:", error)
    attemptReconnect()
  }

  // Return cleanup function
  return () => {
    if (ws) {
      ws.close()
    }
    clearInterval(pingInterval)
    clearTimeout(reconnectTimeoutId)
  }
}

function attemptReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error("Max reconnection attempts reached")
    return
  }

  clearTimeout(reconnectTimeoutId)
  reconnectTimeoutId = window.setTimeout(() => {
    reconnectAttempts++
    console.log(`Attempting to reconnect (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`)
    initWebSocket()
  }, reconnectInterval * Math.pow(2, reconnectAttempts)) // Exponential backoff
}

export function sendMessage(message: any): void {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.error("WebSocket is not open. ReadyState:", ws?.readyState)
    return
  }

  try {
    ws.send(JSON.stringify(message))
  } catch (error) {
    console.error("Error sending message:", error)
  }
}